/* JSON-LD structured data for landing page blocks (ASH-2196).

   Each block builds its schema from the content it actually renders, so the
   markup can never describe items the visitor cannot see. Blocks emit their own
   <script> rather than the page collecting them, matching how the main site's
   FAQ components already do it. */

type JsonObject = Record<string, unknown>

/* CMS text is authored as rich text, so raw values can carry markup and
   entities that would otherwise end up literal in the JSON-LD. */
export function plainText(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, "\u0022")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()
}

export type ReviewInput = {
  reviewerName: string
  quote: string
  /* 0 when the CMS value is missing or unparseable */
  starRating: number
}

/* Bare Review nodes are not valid on their own - each needs the thing it
   reviews - so the reviews are nested under the page as a Product along with an
   aggregate rating. Product rather than Organization deliberately: Google does
   not show review snippets for self-serving Organization or LocalBusiness
   reviews, which is exactly what a testimonial on the seller's own page is.

   Schema ratings start at 1, so unrated reviews are still marked up, just
   without a rating, and are left out of the aggregate. */
export function buildReviewSchema(items: ReviewInput[], itemName: string): JsonObject | null {
  const name = plainText(itemName)
  // schema.org requires an author, so a review with no name cannot be marked up
  const usable = items.filter((item) => item.reviewerName)
  if (!name || usable.length === 0) return null

  const rated = usable.filter((item) => item.starRating >= 1)
  const schema: JsonObject = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    review: usable.map((item) => ({
      "@type": "Review",
      author: { "@type": "Person", name: item.reviewerName },
      ...(item.quote ? { reviewBody: plainText(item.quote) } : {}),
      ...(item.starRating >= 1
        ? {
            reviewRating: {
              "@type": "Rating",
              ratingValue: item.starRating,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
    })),
  }

  if (rated.length > 0) {
    const total = rated.reduce((sum, item) => sum + item.starRating, 0)
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Math.round((total / rated.length) * 10) / 10,
      reviewCount: rated.length,
      bestRating: 5,
      worstRating: 1,
    }
  }

  return schema
}

export type FaqInput = {
  question: string
  answer: string
}

/* FAQPage carries its own content, so unlike Review it needs nothing from the
   page around it. An entry with no answer is dropped: Question requires an
   acceptedAnswer, and an empty one would invalidate the whole block. */
export function buildFaqSchema(items: FaqInput[]): JsonObject | null {
  const usable = items
    .map((item) => ({ question: plainText(item.question), answer: plainText(item.answer) }))
    .filter((item) => item.question && item.answer)
  if (usable.length === 0) return null

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: usable.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  }
}

/** Renders a schema object as a JSON-LD script tag, or nothing when it is null. */
export function JsonLd({ schema }: { schema: JsonObject | null }) {
  if (!schema) return null
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}
